/**
 * Shared chat-file resolution + promote for Delivery Assist Accept
 * (SAD-899 / SAD-904 / SAD-922).
 *
 * All Accept paths that bind a chat file must go through resolveChatFiles so
 * the artifact id stored on the Delivery record comes from
 * attachChatFileForDelivery only. That helper promotes chat-scoped uploads
 * into a program-scoped byte copy (tesoro PromoteChatArtifact); the chat
 * artifact stays owner-only. Do not widen chat visibility.
 *
 * Flow: resolveChatFiles validates (no promote). Call attachChatFileForDelivery
 * (or attachResolvedChatFiles) only when the Delivery write is about to happen,
 * so a failed Accept does not orphan a program copy.
 */ import { artifactClient } from '../../../lib/connect/clients';
import { strListFiles, validateDeliveryEvidenceMeta } from './deliveryEvidenceFiles';
/** PDF-only gate for ScoreDocumentUpload / UploadModificationButton. */ export function validateDeliveryPDFMeta(filename, mimeType, sizeBytes) {
    const base = validateDeliveryEvidenceMeta(filename, mimeType, sizeBytes);
    if (base === 'Empty file' || base === 'File exceeds 250MB limit') return base;
    const mime = (mimeType || '').trim().toLowerCase();
    if (mime === 'application/pdf' || filename.toLowerCase().endsWith('.pdf')) return null;
    return `Unsupported file type: ${mime || 'unknown'} (PDF required)`;
}
/**
 * Chat file ids from agent params. Intentionally omits attachment_id /
 * attachmentId — those names are reserved for an already-bound program
 * artifact on upsert_cpars_score / upsert_qasp_score and must not be
 * re-read as a chat upload (live check: record stored chat id).
 */ export function chatFileIdsFromParams(params) {
    return strListFiles(params, 'files', 'file', 'attachment', 'attachments', 'file_id', 'fileId', 'artifact_id', 'artifactId');
}
/**
 * Returns the artifact id to store on the Delivery / Quality / project record.
 * Promotes a chat-scoped upload to a program-scoped byte copy (SAD-922).
 * Idempotent per (chat artifact, program): tesoro reuses an existing promote.
 * Callers must not bypass this — change only this function to alter bind ids.
 */ export async function attachChatFileForDelivery(file, ctx) {
    let projectId = ctx?.projectId ?? '';
    // Drawer uploads bind zou project_id; resolve it when Accept only has programId
    // so promoted copies land in the same project layout (SAD-925).
    if (!projectId && ctx?.programId) {
        const { deliveryClient } = await import('../../../lib/connect/clients');
        const prog = await deliveryClient.getProgram({
            id: ctx.programId
        });
        projectId = prog.program?.zouProjectId ?? '';
    }
    const resp = await artifactClient.promoteChatArtifact({
        artifactId: file.chatArtifactId || file.artifactId,
        programId: ctx?.programId ?? '',
        projectId
    });
    const id = resp.artifact?.artifactId;
    if (!id) {
        throw new Error(`Could not promote ${file.filename} for Delivery`);
    }
    return id;
}
/** Validate chat uploads (type/size). Does not promote — call attach before write. */ export async function resolveChatFiles(params, opts) {
    const ids = chatFileIdsFromParams(params);
    const names = strListFiles(params, 'file_names', 'fileNames');
    const out = [];
    for(let i = 0; i < ids.length; i++){
        const chatArtifactId = ids[i];
        const meta = await artifactClient.getArtifact({
            artifactId: chatArtifactId
        });
        const art = meta.artifact;
        if (!art) {
            throw new Error(`Chat attachment ${chatArtifactId} was not found`);
        }
        const filename = art.filename || names[i] || chatArtifactId;
        const mimeType = art.mimeType || '';
        const sizeBytes = Number(art.sizeBytes ?? 0);
        const refuse = opts?.pdfOnly ? validateDeliveryPDFMeta(filename, mimeType, sizeBytes) : validateDeliveryEvidenceMeta(filename, mimeType, sizeBytes);
        if (refuse) {
            throw new Error(`${refuse} (${filename})`);
        }
        out.push({
            chatArtifactId,
            artifactId: chatArtifactId,
            filename,
            mimeType,
            sizeBytes
        });
    }
    return out;
}
/** Promote each resolved chat file; returns bind ids for the Delivery write. */ export async function attachResolvedChatFiles(files, ctx) {
    const out = [];
    for (const f of files){
        const artifactId = await attachChatFileForDelivery(f, ctx);
        out.push({
            ...f,
            artifactId
        });
    }
    return out;
}
/**
 * Create a ProgramDocument from a resolved chat file (CorAnswer / LogEvent path).
 * `file.artifactId` must already be the bind id from attachChatFileForDelivery.
 */ export async function createEvidenceProgramDocument(programId, file, opts) {
    const { deliveryClient } = await import('../../../lib/connect/clients');
    const { Timestamp } = await import('@bufbuild/protobuf');
    const today = new Date();
    const resp = await deliveryClient.createProgramDocument({
        programId,
        title: opts?.title || file.filename,
        description: opts?.description ?? 'Evidence attached to a deliverable date decision',
        docDate: Timestamp.fromDate(new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))),
        fileId: file.artifactId,
        fileName: '',
        category: opts?.category ?? 'Other',
        uploadedBy: '',
        companyAssignmentId: opts?.companyAssignmentId ?? '',
        contactId: opts?.contactId ?? '',
        issueId: opts?.issueId ?? ''
    }, opts?.headers ? {
        headers: opts.headers
    } : undefined);
    const id = resp.document?.id;
    if (!id) throw new Error(`Could not record ${file.filename}`);
    return id;
}
